import { c as _c } from "react/compiler-runtime";
import { captureMessage } from '@sentry/browser';
import { useEffect, useRef } from 'react';
import { dissoc } from 'ramda';
import { isNilOrEmpty, isNumber } from 'ramda-adjunct';
import { useHttpCalls } from '../api/useHttpCalls';
import { DEFAULT_SETTINGS } from '../constants/defaultSettings';
import { SMART_SEARCH_KEY } from '../constants/smartSearchKey';
import { useEagGridStore } from '../store/useEagGridStore';
import type { ColumnsSetting, VirtualPreset } from '../types/Api';
import type { QueryIdRequestBody } from '../types/api/data';
import type { EagColumnState } from '../types/EagColumnState';
import type { QueryFilterObject } from '../types/QueryFilterObject';
import type { RowData } from '../types/RowData';
import { buildActivePreset } from '../utils/buildActivePreset';
import { filterTechnicalArray } from '../utils/filterTechnicalArray';
import { getSortModel } from '../utils/getSortModel';
import { getVisibleColumnKeys } from '../utils/getVisibleColumnKeys';
import { isPresetColumnsSettingsInvalid } from '../utils/isPresetColumnsSettingsInvalid';
import { isPresetCustom } from '../utils/isPresetCustom';
import { objectHasEveryEqualPropOfAnother } from '../utils/objectHasEveryEqualPropOfAnother';
import { toIntegerWidth } from '../utils/toIntegerWidth';
import { unwrapFilterValues } from '../utils/unwrapFilterValues';
import { getSearchParamsByGridCode } from '../utils/url/getSearchParamsByGridCode';
import { setSearchParamsByGridCode } from '../utils/url/setSearchParamsByGridCode';
const toColumnsSetting = (cs: EagColumnState): ColumnsSetting => ({
  columnKey: cs.key,
  visible: !cs.hidden,
  width: toIntegerWidth(cs.width),
  pinned: cs.pinned ?? null
});
type UseServerDataParams = {
  gridCode: string;
  queryModifier?: (filter: QueryFilterObject) => QueryFilterObject;
  predefinedColumns: readonly string[];
};
export type UseServerDataResult = {
  fetchPage: (offset: number) => void;
  // rows, totalCount, isLoading, queryId are now in the store
};
export const useServerData = params => {
  const $ = _c(10);
  let t0;
  if ($[0] !== params.gridCode) {
    t0 = {
      gridCode: params.gridCode
    };
    $[0] = params.gridCode;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const http = useHttpCalls(t0);
  const store = useEagGridStore();
  const lastUsedPresetJson = useRef(null);
  const lastUsedComparisonJson = useRef(null);
  const abortControllerRef = useRef(null);
  let t1;
  if ($[2] !== http || $[3] !== params || $[4] !== store) {
    t1 = async fetchOffset => {
      abortControllerRef.current?.abort();
      const controller = new AbortController();
      abortControllerRef.current = controller;
      store.setIsLoadingWithSnapshot();
      const state = store.getState();
      const columnStates = state.columnStates;
      const gridSettings = state.activePreset?.gridSettings ?? DEFAULT_SETTINGS;
      const filterValues = state.filterValues ?? {};
      const dataQuery = state.dataQuery;
      if (!dataQuery) {
        store.clearPreviousRows();
        store.setIsLoading(false);
        return;
      }
      const activePreset = buildActivePreset(dataQuery.id, columnStates, gridSettings);
      const sortModel = getSortModel(columnStates);
      let searchParams = getSearchParamsByGridCode(params.gridCode);
      if (isNilOrEmpty(searchParams)) {
        setSearchParamsByGridCode(params.gridCode, activePreset.dataQueryId);
        searchParams = getSearchParamsByGridCode(params.gridCode);
      }
      const sortBy = sortModel;
      const staticFilters = params.queryModifier ? params.queryModifier({}) : {};
      const modifiedFilters = params.queryModifier ? params.queryModifier(filterValues as QueryFilterObject) : filterValues;
      const limit = gridSettings.itemsPerPage;
      const offset = fetchOffset;
      const rawSmartFilter = (modifiedFilters as Record<string, unknown>)?.[SMART_SEARCH_KEY];
      const smartSearch = rawSmartFilter != null && typeof (rawSmartFilter as Record<string, unknown>)[SMART_SEARCH_KEY] === "string" ? (rawSmartFilter as Record<string, unknown>)[SMART_SEARCH_KEY] as string : null;
      const filtersWithoutSmartSearch = dissoc(SMART_SEARCH_KEY, modifiedFilters);
      const columns = getVisibleColumnKeys(columnStates, params.predefinedColumns);
      const getDataByQueryId = async resolvedQueryId => {
        store.setQueryId(resolvedQueryId);
        setSearchParamsByGridCode(params.gridCode, resolvedQueryId);
        const rowCountResult = await http.getCountByQuery(resolvedQueryId);
        if (isNumber(rowCountResult)) {
          store.setTotalCount(rowCountResult);
        }
        const dataResponse = await http.getDataByQuery(resolvedQueryId, offset, limit, !!smartSearch, columns);
        if (!dataResponse) {
          store.clearPreviousRows();
          store.setIsLoading(false);
          return;
        }
        ;
        try {
          const rowData = dataResponse.map(_temp) as RowData[];
          store.setRows(rowData);
          store.clearPreviousRows();
        } catch (t2) {
          const error = t2;
          if (error instanceof Error) {
            console.error(error.message);
          }
        }
        store.clearPreviousRows();
        store.setIsLoading(false);
      };
      const comparison = {
        smartSearch,
        filters: filtersWithoutSmartSearch,
        sortBy
      };
      const activePresetJson = JSON.stringify(activePreset);
      const comparisonJson = JSON.stringify(comparison);
      const didPresetChange = lastUsedPresetJson.current !== null ? lastUsedPresetJson.current !== activePresetJson : true;
      const isFilterModified = lastUsedComparisonJson.current === null ? true : lastUsedComparisonJson.current !== comparisonJson;
      const existingQueryFiltersFlat = unwrapFilterValues(dataQuery.filters as Record<string, unknown>);
      const isAllStaticFilterPresent = objectHasEveryEqualPropOfAnother(staticFilters, existingQueryFiltersFlat);
      if (didPresetChange && isAllStaticFilterPresent) {
        await getDataByQueryId(activePreset.dataQueryId);
        lastUsedPresetJson.current = activePresetJson;
        lastUsedComparisonJson.current = comparisonJson;
        return;
      }
      if (!isFilterModified && isAllStaticFilterPresent) {
        await getDataByQueryId(searchParams?.queryId ?? activePreset.dataQueryId);
        lastUsedComparisonJson.current = comparisonJson;
        return;
      }
      if (controller.signal.aborted) {
        return;
      }
      const queryIdResponse = await http.createDataQuery({
        filters: unwrapFilterValues(filtersWithoutSmartSearch as Record<string, unknown>) as QueryIdRequestBody['filters'],
        order: sortBy,
        smartSearch: smartSearch ?? null
      });
      if (!queryIdResponse) {
        store.clearPreviousRows();
        store.setIsLoading(false);
        return;
      }
      const newDataQueryId = queryIdResponse.dataQueryId;
      if (!isPresetCustom(activePreset)) {
        const columnsSettings = filterTechnicalArray(columnStates.map(toColumnsSetting));
        const modifiedPreset = {
          columnsSettings,
          gridSettings,
          dataQueryId: newDataQueryId
        };
        if (isPresetColumnsSettingsInvalid(modifiedPreset)) {
          captureMessage("updatePreset", scope => scope.setLevel("error").setExtras({
            userDescription: "update virtual preset from useServerData",
            state: modifiedPreset
          }));
          store.clearPreviousRows();
          store.setIsLoading(false);
          return;
        }
        if (controller.signal.aborted) {
          store.clearPreviousRows();
          store.setIsLoading(false);
          return;
        }
        http.updateVirtualPreset(modifiedPreset).catch(_temp2);
      }
      if (controller.signal.aborted) {
        return;
      }
      await getDataByQueryId(newDataQueryId);
      lastUsedPresetJson.current = activePresetJson;
      lastUsedComparisonJson.current = comparisonJson;
    };
    $[2] = http;
    $[3] = params;
    $[4] = store;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const fetchPage = t1;
  let t2;
  let t3;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => () => {
      abortControllerRef.current?.abort();
    };
    t3 = [];
    $[6] = t2;
    $[7] = t3;
  } else {
    t2 = $[6];
    t3 = $[7];
  }
  useEffect(t2, t3);
  let t4;
  if ($[8] !== fetchPage) {
    t4 = {
      fetchPage
    };
    $[8] = fetchPage;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
};
function _temp(response) {
  return {
    ...response.cells,
    actions: response.actions,
    id: response.id,
    rowNumber: String(response.rowNumber)
  };
}
function _temp2() {}